import { useContext, useState, useRef } from "react";
import { useForm } from "react-hook-form";

import {
  Button,
  Box,
  TextField,
  Typography,
  Dialog,
  DialogActions,
  DialogContent,
} from "@mui/material";

import Edit from '@mui/icons-material/Edit';

import moment from "moment";

import { ReloadContext } from "../../context/ReloadContext";
import { getSearchAreaId, updateArea } from "../../api/area";

import LoadingButton from '@mui/lab/LoadingButton';
import { useReadOnly } from '../../hooks/useReadOnly';

interface Props {
  areaId: number;
}

/**
 * エリア管理ダイアログコンポーネント
 *
 * @param {Props} { AreaId }
 * @return {*} 
 */
export const AreaEditDialog = ({ areaId }: Props) => {
  const isReadOnlyUser = useReadOnly();
  const reloadContext = useContext(ReloadContext);
  const [open, setOpen] = useState(false);
  const { handleSubmit } = useForm<any>();

  const [areaMei, setAreaMei] = useState<string>("");
  const [updateDt, setUpdateDt] = useState<Date | null>(null);
  const [load, setLoad] = useState<boolean>(false);

  // エラーメッセージ表示用
  // 各入力項目ごとのメッセージ
  const [errorMessageAreaMei, setErrorMessageAreaMei] = useState<string>("");

  const isCreate = areaId === 0;
  // 処理中フラグ（ボタン連打対策）
  const processing = useRef(false);

  const handleClickOpen = async () => {
    if (isReadOnlyUser) return;

    // エラーメッセージ初期化
    initErrorMessage();

    if (isCreate) { // 新規登録
      setAreaMei("");
      setUpdateDt(null);
    } else {
      // エリア詳細情報取得
      const res = await getSearchAreaId(areaId);
      if (res?.isError) {
        await reloadContext?.setSnackbarInfo({
          isOpen: true,
          type: "error",
          message: res?.message,
        });
      }
      setAreaMei(res.data[0].area_mei);
      setUpdateDt(res.data[0].update_dt);
    }
    setOpen(true);
  };

  /**
 * エラーメッセージ初期化処理
 */
  const initErrorMessage = () => {
    setErrorMessageAreaMei("")
  }

  const handleClose = () => {
    setOpen(false);
  };

  // テーブル更新・一覧更新
  const handleUpdate = async () => {
    // エラーメッセージ初期化
    initErrorMessage();

    if (areaMei === "") {
      return setErrorMessageAreaMei("エリア名を入力してください。");
    }

    // 処理中(true)なら非同期処理せずに抜ける
    if (processing.current) return;
    // 処理中フラグを上げる
    processing.current = true;
    // 疑似非同期処理
    setTimeout(() => {
      // 処理中フラグを下げる
      processing.current = false;
    }, 1000);
    setLoad(true);
    const result = await updateArea({
      id: areaId,
      area_mei: areaMei,
      update_dt: (updateDt) ? moment(updateDt).format("YYYY-MM-DD HH:mm:ss") : undefined,
    });

    await reloadContext?.setSnackbarInfo({
      isOpen: true,
      type: result.isError ? "error" : "success",
      message: result.message,
    });
    await reloadSetting();
    setLoad(false);
  };

  // モーダル→画面戻り時
  const reloadSetting = () => {
    reloadContext?.setReload(reloadContext?.reload + 1);
    setOpen(false);
  };

  return (
    <Box sx={{ display: "flex" }}>
      {
        isReadOnlyUser && isCreate ? <Box sx={{ mt: 2, mb: 2 }}></Box> :
        isCreate ?
          <Button
            type="button"
            variant="contained"
            sx={{ mt: 2, mb: 2 }}
            onClick={() => handleClickOpen()}
          >
            新規エリア登録
          </Button>
          :
          <Edit color={isReadOnlyUser ? "disabled" : "primary"} fontSize='large'
            onClick={() => handleClickOpen()}
          />
      }
      <Dialog
        open={open}
        PaperProps={{
          sx: {
            width: "50vw",
          }
        }}
      >
        <form onSubmit={handleSubmit(handleUpdate)}>
          <Typography component="h3" variant="h6" mt={1} ml={2}>
            {isCreate ? "新規エリア登録" : "エリア情報編集"}
          </Typography>
          <DialogContent sx={{ paddingTop: 0 }}>
            <TextField
              label="エリア名 *"
              value={areaMei}
              onChange={e => setAreaMei(e.target.value)}
              fullWidth
              variant="outlined"
              sx={{ marginTop: 1 }}
            />
            {errorMessageAreaMei && (
              <small className={"error-message"}>{errorMessageAreaMei}</small>
            )}
          </DialogContent>
          <DialogActions>
            <Button variant="outlined" onClick={handleClose}>閉じる</Button>
            <LoadingButton loading={load} variant="contained" type="submit">登録</LoadingButton>
          </DialogActions>
        </form>
      </Dialog>
    </Box>
  );
}
